// frontend/src/components/Navbar.tsx

import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FileText, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useDocuments } from "../contexts/DocumentsContext";
import { getPatient } from "../services/api";
import api from "../services/api";

const PERMISSION_CHECK_INTERVAL = 30000; // 30 secondi

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateUserPermissions } = useAuth();
  const { refreshDocuments, setDocuments } = useDocuments();
  const [patientName, setPatientName] = useState("Paziente");

  // Fetch patient data when user is authenticated
  useEffect(() => {
    const fetchPatient = async () => {
      if (!user) return; // Non fare la chiamata se non c'è utente

      try {
        const response = await getPatient();
        if (response?.data?.nome && response?.data?.cognome) {
          const fullName = `${response.data.nome} ${response.data.cognome}`;
          setPatientName(fullName);
        }
      } catch (error) {
        // Handle error appropriately
      }
    };

    // Chiama fetchPatient solo se c'è un utente autenticato
    if (user) {
      fetchPatient();
    }
  }, [user]); // Dipende da user invece che essere solo onMount

  // Check permissions on mount and periodically
  useEffect(() => {
    let isMounted = true;
    let interval: NodeJS.Timeout | null = null;

    const checkUpdates = async () => {
      if (!user || !isMounted) return;

      const shouldCheck =
        location.pathname === "/documents" ||
        location.pathname === "/patient" ||
        location.pathname.startsWith("/documents/");

      if (!shouldCheck) return;

      try {
        // 1. Check user permissions
        const userResponse = await api.get("/auth/me");
        if (!isMounted) return;

        const currentUserData = userResponse.data.data;
        const permissionsChanged =
          currentUserData.canDownload !== user.canDownload ||
          currentUserData.isAdmin !== user.isAdmin;

        if (permissionsChanged && isMounted) {
          updateUserPermissions();
        }

        // 2. Check documents for visibility updates
        const docsResponse = await api.get("/documents", {
          params: {
            sort_direction: "DESC",
            admin_view: currentUserData.isAdmin,
          },
        });

        if (isMounted && docsResponse.data.success) {
          setDocuments(docsResponse.data.data);
        }
      } catch (error) {
        // Handle error appropriately
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      }
    };

    if (user && location.pathname.includes("/documents")) {
      checkUpdates();
      interval = setInterval(checkUpdates, PERMISSION_CHECK_INTERVAL);
    }

    return () => {
      isMounted = false;
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [user, location.pathname, updateUserPermissions, refreshDocuments]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <nav className="bg-white border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo/Home link */}
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center gap-2 font-semibold text-lg text-gray-900"
            >
              <FileText size={24} className="text-blue-600" />
              <span>{user ? `Documenti ${patientName}` : "MedApp by CL"}</span>
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="flex items-center gap-6">
            {user && (
              <>
                <Link
                  to="/patient"
                  className="text-gray-600 hover:text-gray-900"
                >
                  Scheda Paziente
                </Link>

                <button
                  onClick={handleLogout}
                  className="text-gray-700 hover:text-gray-900"
                >
                  <div className="flex items-center gap-2">
                    <span>
                      {user.nome} {user.cognome}
                    </span>
                    <LogOut size={16} className="text-gray-600" />
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
